<script lang="ts">
  import type {
    ButtonSize,
    TextColor,
    ButtonType,
    ButtonVariant,
    ButtonWidth,
    IconPosition
  } from '$components/mds/Buttons';

  import {
    buttonVariantClasses,
    buttonTextColorClasses,
    buttonHeightClasses,
    buttonTextClasses,
    buttonWidthClasses
  } from '$components/mds/Buttons';
  import LoadingSpinner from '$mds/LoadingSpinner';
  import IconWrapper, { IconWrapperTheme, type IconComponentType } from '$components/mds/IconWrapper';
  import { type ActionArray, useActions } from '$components/actions';

  export let variant: ButtonVariant = 'primary';
  export let textColor: TextColor = 'action';
  export let iconClasses = '';
  export let size: ButtonSize = 'md';
  export let iconPosition: IconPosition = 'left';
  export let iconTheme: IconWrapperTheme = 'default';
  export let width: ButtonWidth = 'min';
  export let icon: IconComponentType | null = null;
  export let div = false;
  export let truncateWrapperClass = '';
  /** an array of actions to add to the button, actions are a tuple of `[action, props]`
   * @example use={[[ tooltip, { ...props }]]}
   */
  export let use: ActionArray = [];

  export let disabled = false;
  export let loading = false;
  $: isDisabled = disabled || loading;
  $: disabledVariant = isDisabled ? 'disabled' : 'default';

  export let type: ButtonType = 'button';
  export let href: string | undefined = undefined;
  export let external = false;
  export let title: string | undefined = undefined;
  export let tabindex: number = 0;

  // TODO: Remove this once we have a better way to handle black/white text
  export let className = '';
  export { className as class };

  $: buttonProps = { type, disabled: isDisabled };
  const anchorProps = {
    href,
    role: 'button',
    ...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})
  };

  let classes = '';
  $: if (width || variant || disabledVariant || textColor || size || iconPosition || className) {
    classes = ''; // reset classes on every update to avoid chaining
    classes += ` ${iconPosition === 'right' ? 'flex-row-reverse' : ''}`;
    classes += ` ${buttonVariantClasses[variant][disabledVariant]}`;
    classes += ` ${variant === 'ghost' ? buttonTextColorClasses[textColor][disabledVariant] : ''}`;
    classes += ` ${buttonWidthClasses[width]} ${buttonHeightClasses[size]} ${buttonTextClasses[size]}`;
    classes += ` ${className}`;
  }
</script>

<svelte:element
  this={href ? 'a' : div ? 'div' : 'button'}
  role={href ? 'link' : div ? 'presentation' : 'button'}
  {title}
  {tabindex}
  {...href ? anchorProps : buttonProps}
  class="focus-visible:ring-ondark-focused inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-5 text-center transition-all duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 {classes}"
  class:pointer-events-none={isDisabled}
  class:cursor-not-allowed={isDisabled}
  class:relative={isDisabled && use?.length}
  use:useActions={use}
  on:click
  on:keyup
  on:keydown
  on:mouseenter
  on:mouseleave
>
  {#if loading}
    <div class={iconPosition === 'none' ? 'absolute' : ''}>
      <LoadingSpinner
        theme={variant === 'ghost' ? buttonTextColorClasses[textColor].spinner : buttonVariantClasses[variant].spinner}
      />
    </div>
  {:else if icon && iconPosition !== 'none'}
    <IconWrapper class={iconClasses} {icon} theme={iconTheme} />
  {/if}
  <div
    class="{truncateWrapperClass} {!loading || (loading && iconPosition !== 'none')
      ? ''
      : 'pointer-events-none opacity-0'} truncate"
  >
    <slot>Button</slot>
  </div>
  {#if isDisabled && use?.length}
    <span class="pointer-events-auto absolute -inset-0" use:useActions={use}></span>
  {/if}
</svelte:element>
